import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardMedia,
  Chip, Button, CircularProgress, TextField, InputAdornment,
  IconButton, Tooltip, Alert, Divider, Badge, Tab, Tabs,
} from '@mui/material';
import {
  Search, Refresh, Bookmark, BookmarkBorder,
  OpenInNew, AccessTime, FilterList, Newspaper,
  RocketLaunch, Public, Science, Star,
} from '@mui/icons-material';

// ─── Category config ──────────────────────────────────────────────────────────
const CATEGORIES = [
  { label: 'All',     icon: <Newspaper sx={{ fontSize:14 }} />,     color: '#4fc3f7', keywords: [] },
  { label: 'NASA',    icon: <Public sx={{ fontSize:14 }} />,         color: '#66bb6a', keywords: ['nasa'] },
  { label: 'SpaceX',  icon: <RocketLaunch sx={{ fontSize:14 }} />,   color: '#ffa726', keywords: ['spacex'] },
  { label: 'Mars',    icon: <Public sx={{ fontSize:14 }} />,         color: '#ef5350', keywords: ['mars'] },
  { label: 'Moon',    icon: <Star sx={{ fontSize:14 }} />,           color: '#e0e0e0', keywords: ['moon','lunar','artemis'] },
  { label: 'Science', icon: <Science sx={{ fontSize:14 }} />,        color: '#ab47bc', keywords: ['exoplanet','telescope','discovery','astronomy','black hole','galaxy'] },
];

// ─── Format date ──────────────────────────────────────────────────────────────
function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' });
}
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1)  return 'Just now';
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(dateStr);
}

// ─── News Card ────────────────────────────────────────────────────────────────
function NewsCard({ article, bookmarks, onBookmark }) {
  const isBookmarked = bookmarks.includes(article.id);
  const [imgError, setImgError]   = useState(false);

  const siteName = (() => {
    try { return new URL(article.url).hostname.replace('www.', ''); }
    catch { return article.news_site || 'Source'; }
  })();

  return (
    <Card sx={{
      height: '100%', display: 'flex', flexDirection: 'column',
      border: '1px solid rgba(255,255,255,0.12)',
      transition: 'all 0.25s',
      position: 'relative', overflow: 'hidden',
      '&:hover': {
        border: '1px solid rgba(255,255,255,0.35)',
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 32px rgba(255,255,255,0.1)',
      },
    }}>
      {/* Image */}
      {article.image_url && !imgError ? (
        <CardMedia
          component="img"
          height="180"
          image={article.image_url}
          alt={article.title}
          onError={() => setImgError(true)}
          sx={{ objectFit: 'cover', bgcolor: 'rgba(5,13,26,0.8)' }}
        />
      ) : (
        <Box sx={{
          height: 180, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 56,
          bgcolor: 'rgba(5,13,26,0.6)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          🚀
        </Box>
      )}

      {/* Bookmark button */}
      <Tooltip title={isBookmarked ? 'Remove bookmark' : 'Bookmark'}>
        <IconButton
          onClick={() => onBookmark(article.id)}
          size="small"
          sx={{
            position: 'absolute', top: 8, right: 8,
            bgcolor: 'rgba(5,13,26,0.8)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: isBookmarked ? '#ffd600' : 'rgba(255,255,255,0.6)',
            '&:hover': { bgcolor: 'rgba(5,13,26,0.95)', color: '#ffd600' },
          }}
        >
          {isBookmarked ? <Bookmark fontSize="small" /> : <BookmarkBorder fontSize="small" />}
        </IconButton>
      </Tooltip>

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        {/* Source + time */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Chip
            label={article.news_site || siteName}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light',
              border: '1px solid rgba(255,255,255,0.2)',
              fontSize: 9, fontWeight: 700, height: 18,
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <AccessTime sx={{ fontSize: 11, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
              {timeAgo(article.published_at)}
            </Typography>
          </Box>
        </Box>

        {/* Title */}
        <Typography variant="body2" sx={{
          fontWeight: 700, color: 'text.primary',
          lineHeight: 1.5, mb: 1, flex: 1,
          display: '-webkit-box', WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {article.title}
        </Typography>

        {/* Summary */}
        {article.summary && (
          <Typography variant="caption" sx={{
            color: 'text.secondary', lineHeight: 1.6,
            display: '-webkit-box', WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
            mb: 1.5,
          }}>
            {article.summary}
          </Typography>
        )}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1.5 }} />

        {/* Read more */}
        <Button
          fullWidth size="small" variant="outlined"
          endIcon={<OpenInNew sx={{ fontSize: 13 }} />}
          href={article.url} target="_blank" rel="noopener noreferrer"
          sx={{
            borderColor: 'rgba(255,255,255,0.3)', color: 'primary.light',
            fontSize: 11, fontWeight: 700, textTransform: 'none',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(255,255,255,0.08)' },
          }}
        >
          Read Full Article
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SpaceNewsPage() {
  const [articles, setArticles]       = useState([]);
  const [filtered, setFiltered]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');
  const [category, setCategory]       = useState('All');
  const [bookmarks, setBookmarks]     = useState(() => {
    try { return JSON.parse(localStorage.getItem('spaceNewsBookmarks') || '[]'); }
    catch { return []; }
  });
  const [tab, setTab]                 = useState(0); // 0=All, 1=Bookmarked
  const [lastRefresh, setLastRefresh] = useState(null);
  const [nextRefresh, setNextRefresh] = useState(600);
  const timerRef                      = useRef(null);
  const countdownRef                  = useRef(null);

  // ── Fetch news ──────────────────────────────────────────────────────────────
  const fetchNews = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError('');
    try {
      const res  = await fetch(
        'https://api.spaceflightnewsapi.net/v4/articles/?limit=60&ordering=-published_at'
      );
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();
      setArticles(data.results || []);
      setLastRefresh(new Date());
      setNextRefresh(600);
    } catch (e) {
      console.error(e);
      setError('Failed to load space news. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Auto refresh every 10 minutes ──────────────────────────────────────────
  useEffect(() => {
    fetchNews();

    timerRef.current = setInterval(() => fetchNews(false), 600000);

    countdownRef.current = setInterval(() => {
      setNextRefresh(n => n <= 1 ? 600 : n - 1);
    }, 1000);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [fetchNews]);

  // ── Filter logic ────────────────────────────────────────────────────────────
  useEffect(() => {
    let result = [...articles];

    // Category filter
    if (category !== 'All') {
      const cat = CATEGORIES.find(c => c.label === category);
      if (cat?.keywords.length) {
        result = result.filter(a =>
          cat.keywords.some(kw =>
            a.title?.toLowerCase().includes(kw) ||
            a.summary?.toLowerCase().includes(kw) ||
            a.news_site?.toLowerCase().includes(kw)
          )
        );
      }
    }

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        a.title?.toLowerCase().includes(q) ||
        a.summary?.toLowerCase().includes(q) ||
        a.news_site?.toLowerCase().includes(q)
      );
    }

    // Bookmarks tab
    if (tab === 1) {
      result = result.filter(a => bookmarks.includes(a.id));
    }

    setFiltered(result);
  }, [articles, search, category, bookmarks, tab]);

  // ── Bookmark toggle ─────────────────────────────────────────────────────────
  const toggleBookmark = (id) => {
    setBookmarks(prev => {
      const updated = prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id];
      localStorage.setItem('spaceNewsBookmarks', JSON.stringify(updated));
      return updated;
    });
  };

  const formatCountdown = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', mb:2.5, flexWrap:'wrap', gap:1.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight:800 }}>
            📰 Space News
          </Typography>
          <Typography variant="body2" sx={{ color:'text.secondary', mt:0.5 }}>
            Real-time space discoveries, missions & astronomy updates
          </Typography>
        </Box>
        <Box sx={{ display:'flex', gap:1.5, alignItems:'center', flexWrap:'wrap' }}>
          {lastRefresh && (
            <Typography variant="caption" sx={{ color:'text.secondary' }}>
              Refreshes in {formatCountdown(nextRefresh)}
            </Typography>
          )}
          <Chip
            label="● LIVE"
            size="small"
            sx={{
              bgcolor:'rgba(102,187,106,0.15)', color:'#66bb6a',
              border:'1px solid rgba(102,187,106,0.4)', fontWeight:800,
              animation:'pulse 2s infinite',
              '@keyframes pulse':{ '0%,100%':{ opacity:1 },'50%':{ opacity:0.5 } },
            }}
          />
          <Tooltip title="Refresh now">
            <IconButton onClick={() => fetchNews()} size="small"
              sx={{ border:'1px solid rgba(255,255,255,0.3)', color:'primary.light' }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Stats row ──────────────────────────────────────────────────────── */}
      <Grid container spacing={1.5} sx={{ mb:2.5 }}>
        {[
          { label:'Total Articles', value: articles.length,                     color:'#4fc3f7' },
          { label:'Showing',        value: filtered.length,                     color:'#66bb6a' },
          { label:'Bookmarked',     value: bookmarks.length,                    color:'#ffd600' },
          { label:'Last Updated',   value: lastRefresh ? timeAgo(lastRefresh) : '---', color:'#ab47bc' },
        ].map(({ label, value, color }) => (
          <Grid item xs={6} sm={3} key={label}>
            <Card sx={{ border:`1px solid ${color}22`, textAlign:'center' }}>
              <CardContent sx={{ p:1.5, '&:last-child':{ pb:1.5 } }}>
                <Typography variant="h6" sx={{ fontWeight:800, color }}>{value}</Typography>
                <Typography variant="caption" sx={{ color:'text.secondary', fontSize:10 }}>{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Search + Filters ────────────────────────────────────────────────── */}
      <Card sx={{ mb:2.5, border:'1px solid rgba(255,255,255,0.12)' }}>
        <CardContent sx={{ p:2 }}>
          <Box sx={{ display:'flex', gap:2, flexWrap:'wrap', alignItems:'center' }}>
            {/* Search */}
            <TextField
              placeholder="Search space news..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth:260, flex:1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color:'text.secondary', fontSize:18 }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Category chips */}
            <Box sx={{ display:'flex', gap:0.8, flexWrap:'wrap', alignItems:'center' }}>
              <FilterList sx={{ color:'text.secondary', fontSize:18 }} />
              {CATEGORIES.map(cat => (
                <Chip
                  key={cat.label}
                  icon={cat.icon}
                  label={cat.label}
                  onClick={() => setCategory(cat.label)}
                  size="small"
                  sx={{
                    cursor:'pointer', fontWeight:700, fontSize:11,
                    bgcolor: category === cat.label ? `${cat.color}22` : 'transparent',
                    color:   category === cat.label ? cat.color : 'text.secondary',
                    border:  category === cat.label
                      ? `1px solid ${cat.color}`
                      : '1px solid rgba(255,255,255,0.1)',
                    '& .MuiChip-icon': {
                      color: category === cat.label ? cat.color : 'text.secondary',
                    },
                    transition:'all 0.2s',
                  }}
                />
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ── Tabs: All / Bookmarked ──────────────────────────────────────────── */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb:2.5,
        '& .MuiTab-root':{ color:'text.secondary', fontWeight:600, textTransform:'none' },
        '& .Mui-selected':{ color:'primary.light' },
        '& .MuiTabs-indicator':{ bgcolor:'primary.main' } }}>
        <Tab label={`All News (${filtered.length})`} />
        <Tab label={
          <Badge badgeContent={bookmarks.length} color="warning" sx={{ '& .MuiBadge-badge':{ fontSize:9 } }}>
            <Box sx={{ pr: bookmarks.length > 0 ? 1.5 : 0 }}>⭐ Bookmarked</Box>
          </Badge>
        } />
      </Tabs>

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb:2.5, bgcolor:'rgba(239,83,80,0.1)', border:'1px solid rgba(239,83,80,0.3)' }}
          action={<Button color="error" size="small" onClick={() => fetchNews()}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {/* ── Loading ─────────────────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', py:10, gap:2 }}>
          <CircularProgress sx={{ color:'primary.main' }} />
          <Typography variant="body2" sx={{ color:'text.secondary' }}>
            Fetching latest space news...
          </Typography>
        </Box>

      ) : tab === 1 && bookmarks.length === 0 ? (
        <Box sx={{ textAlign:'center', py:10 }}>
          <Typography sx={{ fontSize:48, mb:2 }}>⭐</Typography>
          <Typography variant="h6" sx={{ color:'text.secondary', mb:1 }}>No bookmarks yet</Typography>
          <Typography variant="body2" sx={{ color:'text.secondary' }}>
            Click the bookmark icon on any article to save it here
          </Typography>
        </Box>

      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign:'center', py:10 }}>
          <Typography sx={{ fontSize:48, mb:2 }}>🔍</Typography>
          <Typography variant="h6" sx={{ color:'text.secondary', mb:1 }}>No results found</Typography>
          <Typography variant="body2" sx={{ color:'text.secondary' }}>
            Try a different search term or category
          </Typography>
          <Button sx={{ mt:2 }} onClick={() => { setSearch(''); setCategory('All'); }}>
            Clear Filters
          </Button>
        </Box>

      ) : (
        <Grid container spacing={2.5}>
          {filtered.map(article => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={article.id}>
              <NewsCard
                article={article}
                bookmarks={bookmarks}
                onBookmark={toggleBookmark}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      {!loading && filtered.length > 0 && (
        <Box sx={{ textAlign:'center', mt:4, pb:2 }}>
          <Typography variant="caption" sx={{ color:'text.secondary' }}>
            Powered by Spaceflight News API • Auto-refreshes every 10 minutes
          </Typography>
        </Box>
      )}
    </Box>
  );
}
